import React, { createContext, useContext, useEffect, useLayoutEffect, useMemo } from 'react';
import { LSKEY_INITIAL_URL } from 'src/utils/auth';
import { UserOptionType } from 'src/api/types';
import { featuresMap } from './util';
import { useNavigate } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { setCurrencySymbol } from 'src/utils/misc';
import {
  FlatOrganisationsNavQuery,
  ProfileQuery,
  useFlatOrganisationsNavLazyQuery,
  useProfileLazyQuery,
} from 'src/graphql/_generated';
import useIsRealUser from './useIsRealUser';
import { ApolloError } from '@apollo/client';
import { useAuth0 } from '@auth0/auth0-react';
import { initializeTokenService, clearTokenService } from 'src/utils/tokenService';
import { makeLogout } from 'src/utils/auth';

interface AuthContextInterface {
  usersOptios: UserOptionType[];
  isSignedIn: boolean;
  isLoading: boolean;
  appLoaded: boolean;
  isPlatformAdmin: boolean;
  isPartnerAdmin: boolean;
  isTscsRequiredToAccept: boolean;
  profile: ProfileQuery['profile'] | undefined;
  flatOrganisations: FlatOrganisationsNavQuery['flatOrganisations'];
  features: ReturnType<typeof featuresMap>;
  workspaceId: string;
  orgId: string;
  getRealUserId: (email?: string | null) => string | null;
  error: ApolloError | undefined;
}

const AuthContext = createContext<Partial<AuthContextInterface>>({});

const AuthProvider = (props: React.PropsWithChildren) => {
  const navigate = useNavigate();
  const { isAuthenticated, isLoading: auth0Loading, getAccessTokenSilently, logout: logoutAuth0 } = useAuth0();

  const [getProfile, { data: profile, error: profileError }] = useProfileLazyQuery();
  const [getFlatOrganisations, { data: flatOrganisations, error: flatOrganisationsError }] =
    useFlatOrganisationsNavLazyQuery();
  const { getRealUserId } = useIsRealUser({
    workspaceId: profile?.profile.activeWorkspace?.id,
    orgId: profile?.profile.activeOrganisation?.id,
  });

  const performFullLogout = () => {
    makeLogout({ noEvents: true });
    logoutAuth0({
      logoutParams: { returnTo: `${window.location.protocol}//${window.location.host}/auth` },
    });
  };

  useEffect(() => {
    if (isAuthenticated && !auth0Loading) {
      initializeTokenService(getAccessTokenSilently, performFullLogout);
    } else if (!isAuthenticated && !auth0Loading) {
      clearTokenService();
    }
  }, [isAuthenticated, auth0Loading, getAccessTokenSilently, logoutAuth0]);

  useEffect(() => {
    if (isAuthenticated && !auth0Loading) {
      getProfile().then(({ data }) => {
        if (data?.profile?.user) {
          Sentry.setUser({
            id: data.profile.user.id,
            organisation_id: data.profile.activeOrganisation?.id,
            workspace_id: data.profile.activeWorkspace?.id,
          });
          setCurrencySymbol(data?.profile?.activeWorkspace?.currency);
          getFlatOrganisations({ variables: { includeArchived: false } });
        }
      });
    }
  }, [isAuthenticated, auth0Loading, getProfile, getFlatOrganisations]);

  useLayoutEffect(() => {
    if (!auth0Loading && isAuthenticated && profile?.profile.activeWorkspace && flatOrganisations) {
      const initialUrl = window.localStorage.getItem(LSKEY_INITIAL_URL);

      if (initialUrl) {
        const invites = profile.profile?.user?.invites || [];
        if (
          flatOrganisations!.flatOrganisations.filter((o) => o.recordType === 'W' && !o.isArchived).length === 1 &&
          invites.length === 0
        ) {
          navigate(initialUrl || '/risk-assessments', { replace: true });
        } else {
          navigate(initialUrl || '/home', { replace: true });
        }

        window.localStorage.removeItem(LSKEY_INITIAL_URL);
      }
    }
  }, [auth0Loading, isAuthenticated, profile, flatOrganisations, navigate]);

  const workspaceId = profile?.profile.activeWorkspace?.id;

  const features = useMemo(() => featuresMap(profile?.profile?.features || []), [profile?.profile?.features]);

  const flatOrgs = useMemo(() => flatOrganisations?.flatOrganisations || [], [flatOrganisations]);

  const value = useMemo<Partial<AuthContextInterface>>(
    () => ({
      usersOptios: profile?.profile.activeWorkspace?.owners || [],
      isSignedIn: isAuthenticated,
      isLoading: auth0Loading,
      appLoaded: Boolean(!auth0Loading && isAuthenticated && profile && flatOrganisations),
      error: profileError || flatOrganisationsError,
      isPlatformAdmin: profile?.profile?.permissions.isPlatformAdmin,
      isPartnerAdmin: profile?.profile?.permissions.isPartnerAdmin,
      isTscsRequiredToAccept:
        profile?.profile?.terms.status === 'REQUIRED' || profile?.profile?.terms.status === 'NOT_AUTHORISED_TO_ACCEPT',
      profile: profile?.profile,
      flatOrganisations: flatOrgs,
      features,
      workspaceId,
      orgId: profile?.profile.activeOrganisation?.id,
      getRealUserId,
    }),
    [
      profile,
      isAuthenticated,
      auth0Loading,
      flatOrganisations,
      flatOrgs,
      features,
      workspaceId,
      getRealUserId,
      profileError,
      flatOrganisationsError,
    ],
  );

  return <AuthContext.Provider value={value} {...props} />;
};

const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within a AuthProvider');
  }
  return context as AuthContextInterface;
};

export { AuthProvider, useAuth };
